import { useState } from 'react';
import {
  SURVEY_ANSWER_MIRROR_LIMIT,
  normalizePromoSurvey,
  type InterestLevel,
  type InterestStage,
  type PromoEventType,
  type PromoInterestKey,
  type PromoSurvey,
  type PromoSurveyChoiceQuestion,
  type SegmentSpec,
  type WashpassStatus,
} from '@kawai/shared';
import { Input, Select } from '@kawai/ui';
import {
  INTEREST_KEY_OPTIONS,
  INTEREST_LEVEL_OPTIONS,
  SEGMENT_EVENT_TYPE_OPTIONS,
  SEGMENT_MIN_STAGE_OPTIONS,
  WASHPASS_STATUS_OPTIONS,
} from '../lib/segment.js';
import {
  COUPON_HOLDING_STATE_OPTIONS,
  buildCouponHoldingSegment,
  findCouponSegmentOption,
  type CouponSegmentOption,
} from '../lib/couponSegmentOptions.js';
import type { CouponHoldingSegmentState, FirstSourceBucket } from '@kawai/shared';
import { FIRST_SOURCE_OPTIONS } from '../lib/firstSource.js';

interface SelectOption {
  value: string;
  label: string;
}

interface SegmentFilterBarProps {
  value: SegmentSpec;
  onChange: (next: SegmentSpec) => void;
  /**
   * 「予約済みメニュー / カテゴリ」select の選択肢（M-37）。
   * 未指定（読み込み前など）はテキスト入力にフォールバックする。
   */
  menuOptions?: SelectOption[];
  categoryOptions?: SelectOption[];
  /**
   * M-76: 興味タグを選んだときに「商談中のお客様を除外」を自動で ON にするか（既定 false）。
   *
   * **配信作成（SegmentComposePage）のみ true** にする — 商談への割込みを既定で防ぐため。
   * 顧客一覧（CustomersListPage）と販促プランの KPI 母数（PromoPlanEditPage）は false のまま:
   * 一覧から商談中の顧客が消えたり KPI の母数が変動したりするのは絞り込みの意味が変わる。
   */
  suppressHotByDefault?: boolean;
  /**
   * M-97: アンケート回答での絞り込みに使うアンケート定義。
   * 未指定（読み込み前など）はその条件の UI を出さない（既に条件が入っている場合のみ
   * 解除導線を出す — 見えない条件が効いたままにしない）。
   */
  surveys?: readonly PromoSurvey[];
  /**
   * M-166: クーポンの保有状態で絞る条件の選択肢（ルーレットの用途 = 参加賞 / 1〜4 等）。
   * 未指定ならその行を出さない — 判定材料（coupon_usages → lineUserId 集合）を解決する経路が
   * ある画面（配信作成 / 顧客一覧）だけが渡す。自動配信設定・シナリオは日次評価に集合解決が
   * 無いので渡さない（functions 側の strict schema も弾く = 見えない条件が効いたままにならない）。
   * 既に条件が入っているのに選択肢が無い場合は解除導線だけ出す（surveys と同じ方針）。
   */
  couponOptions?: readonly CouponSegmentOption[];
}

/**
 * select 用の option を構築する。現在値がマスタに無い（削除済み等）場合でも
 * 選択状態を失わないよう、先頭に現在値の option を補う。
 */
function withCurrentValue(
  options: SelectOption[],
  current: string | undefined,
): SelectOption[] {
  if (!current || options.some((o) => o.value === current)) return options;
  return [{ value: current, label: `${current}（マスタ外）` }, ...options];
}

/**
 * LE-00 (promo): セグメント抽出条件 UI（一覧の絞り込み / 配信作成で共用）。
 *
 * すべての条件は optional。指定された条件のみ AND で評価される（`matchesSegment`）。
 * 入力値は immutable に親へ伝播（既存 value を破壊しない）。
 */
export function SegmentFilterBar({
  value,
  onChange,
  menuOptions,
  categoryOptions,
  suppressHotByDefault = false,
  surveys,
  couponOptions,
}: SegmentFilterBarProps) {
  const update = (patch: Partial<SegmentSpec>): void => {
    onChange({ ...value, ...patch });
  };

  const toggleWashpass = (status: WashpassStatus): void => {
    const current = value.washpassStatus ?? [];
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    update({ washpassStatus: next.length > 0 ? next : undefined });
  };

  return (
    <fieldset className="space-y-5">
      <legend className="text-h-card font-semibold text-ink">セグメント条件</legend>

      <div className="grid gap-4 rounded-[10px] bg-surface-2 px-[22px] py-5 sm:grid-cols-2">
        {/* 興味タグ */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">興味タグ</span>
          <Select
            surface="white"
            aria-label="興味タグ"
            value={value.interestKey ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              update({
                interestKey: v ? (v as PromoInterestKey) : undefined,
                // 興味キーを外したら minLevel / minStage / churnRisk /
                // suppressHotInProgress もクリア（いずれも interestKey 従属条件）
                minLevel: v ? value.minLevel : undefined,
                minStage: v ? value.minStage : undefined,
                churnRisk: v ? value.churnRisk : undefined,
                // M-76: 配信作成では商材を選んだ時点で「商談中を除外」を既定 ON にする
                // （既に明示値があればそれを尊重 = ユーザーが外した意思を上書きしない）。
                suppressHotInProgress: v
                  ? (value.suppressHotInProgress ??
                    (suppressHotByDefault && !value.interestKey ? true : undefined))
                  : undefined,
              });
            }}
          >
            <option value="">指定なし</option>
            {INTEREST_KEY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        {/* 最低確度（興味キー指定時のみ有効） */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">最低確度</span>
          <Select
            surface="white"
            aria-label="最低確度"
            value={value.minLevel ?? ''}
            disabled={!value.interestKey}
            onChange={(e) => {
              const v = e.target.value;
              update({ minLevel: v ? (v as InterestLevel) : undefined });
            }}
          >
            <option value="">指定なし（推定以上）</option>
            {INTEREST_LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        {/* 最低ステージ（M-72 / 興味キー指定時のみ有効。評価時減衰後の実効ステージで判定） */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">最低ステージ</span>
          <Select
            surface="white"
            aria-label="最低ステージ"
            value={value.minStage ?? ''}
            disabled={!value.interestKey}
            onChange={(e) => {
              const v = e.target.value;
              update({ minStage: v ? (v as InterestStage) : undefined });
            }}
          >
            <option value="">指定なし（全員）</option>
            {SEGMENT_MIN_STAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}以上
              </option>
            ))}
          </Select>
        </label>

        {/* 呼び戻し対象（M-75 / タイプ③離反検知。興味キー指定時のみ有効・評価時判定） */}
        <label className="flex items-center gap-2 self-end pb-1 text-label text-ink-2">
          <input
            type="checkbox"
            aria-label="呼び戻し対象"
            disabled={!value.interestKey}
            checked={value.churnRisk === true}
            onChange={(e) => {
              update({ churnRisk: e.target.checked ? true : undefined });
            }}
          />
          呼び戻し対象（来店間隔の逸脱）
        </label>

        {/*
          M-76 §7 排他制御: 担当者が商談中（HOT 案件が「対応中」）のお客様を配信対象から外す。
          興味キー指定時のみ有効（排他は顧客×商材単位のため商材が定まらないと判定できない）。
          配信作成では既定 ON（SegmentComposePage の初期値）— 商談への割込みを既定で防ぐ。
        */}
        <label className="flex items-center gap-2 self-end pb-1 text-label text-ink-2">
          <input
            type="checkbox"
            aria-label="商談中のお客様を除外"
            disabled={!value.interestKey}
            checked={value.suppressHotInProgress === true}
            onChange={(e) => {
              update({ suppressHotInProgress: e.target.checked ? true : undefined });
            }}
          />
          商談中のお客様を除外（HOT案件が対応中）
        </label>

        {/* 最終来店からの日数 */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">
            休眠日数（N 日以上行動なし）
          </span>
          <Input
            surface="white"
            type="number"
            min={0}
            aria-label="休眠日数"
            placeholder="例: 90"
            value={
              typeof value.inactiveSinceDays === 'number' ? value.inactiveSinceDays : ''
            }
            onChange={(e) => {
              const raw = e.target.value;
              update({
                inactiveSinceDays: raw === '' ? undefined : Math.max(0, Number(raw)),
              });
            }}
          />
        </label>

        {/* 車検期限しきい値 */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">
            車検期限（N 日以内に到来）
          </span>
          {/* M-79: アンケートの自己申告は「年・月」までの精度で、月末として保存される。
              30 日より短い窓だと窓全体が実際の満了日より後ろにずれうるため注意を促す。 */}
          <span className="block text-note text-ink-3">
            アンケートで集めた満了日は月単位（月末扱い）です。30 日以上の余裕をもった値を
            指定してください。
          </span>
          <Input
            surface="white"
            type="number"
            min={0}
            aria-label="車検期限しきい値"
            placeholder="例: 60"
            value={
              typeof value.inspectionDueWithinDays === 'number'
                ? value.inspectionDueWithinDays
                : ''
            }
            onChange={(e) => {
              const raw = e.target.value;
              update({
                inspectionDueWithinDays:
                  raw === '' ? undefined : Math.max(0, Number(raw)),
              });
            }}
          />
        </label>

        {/* 手動タグ */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">手動タグ（promoTag）</span>
          <Input
            surface="white"
            type="text"
            aria-label="手動タグ"
            placeholder="例: vip"
            value={value.promoTag ?? ''}
            onChange={(e) => {
              const v = e.target.value.trim();
              update({ promoTag: v === '' ? undefined : v });
            }}
          />
        </label>
      </div>

      {/* WashPass 状態（複数 OR） */}
      <div className="space-y-2 rounded-[10px] bg-surface-2 px-[22px] py-5">
        <span className="text-label font-medium text-ink-2">WashPass 状態</span>
        <div className="flex flex-wrap gap-3">
          {WASHPASS_STATUS_OPTIONS.map((o) => {
            const checked = (value.washpassStatus ?? []).includes(o.value);
            return (
              <label
                key={o.value}
                className="flex items-center gap-1.5 text-body text-ink-2"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleWashpass(o.value)}
                  className="rounded-sm text-brand focus:ring-brand"
                />
                {o.label}
              </label>
            );
          })}
        </div>
      </div>

      {/* 予約履歴（M-37） */}
      <div className="grid gap-4 rounded-[10px] bg-surface-2 px-[22px] py-5 sm:grid-cols-2">
        {/* 予約回数 */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">予約回数（N 回以上）</span>
          <Input
            surface="white"
            type="number"
            min={1}
            aria-label="予約回数"
            placeholder="例: 1"
            disabled={value.neverBooked === true}
            value={typeof value.minBookingCount === 'number' ? value.minBookingCount : ''}
            onChange={(e) => {
              const raw = e.target.value;
              update({
                minBookingCount: raw === '' ? undefined : Math.max(1, Number(raw)),
              });
            }}
          />
        </label>

        {/* 未予約のみ */}
        <label className="flex items-end gap-2 pb-2 text-body text-ink-2">
          <input
            type="checkbox"
            aria-label="未予約のみ"
            checked={value.neverBooked === true}
            onChange={(e) => {
              const checked = e.target.checked;
              // 未予約と「予約あり」系は矛盾するため、ON 時は予約条件をクリア
              update(
                checked
                  ? {
                      neverBooked: true,
                      minBookingCount: undefined,
                      bookedCategoryId: undefined,
                      bookedMenuId: undefined,
                    }
                  : { neverBooked: undefined },
              );
            }}
            className="mb-0.5 rounded-sm text-brand focus:ring-brand"
          />
          未予約の顧客のみ
        </label>

        {/* 予約済みメニュー */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">予約済みメニュー</span>
          {menuOptions ? (
            <Select
              surface="white"
              aria-label="予約済みメニュー"
              disabled={value.neverBooked === true}
              value={value.bookedMenuId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                update({ bookedMenuId: v ? v : undefined });
              }}
            >
              <option value="">指定なし</option>
              {withCurrentValue(menuOptions, value.bookedMenuId).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              surface="white"
              type="text"
              aria-label="予約済みメニュー"
              placeholder="例: visit_washpass"
              disabled={value.neverBooked === true}
              value={value.bookedMenuId ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                update({ bookedMenuId: v === '' ? undefined : v });
              }}
            />
          )}
        </label>

        {/* 予約済みカテゴリ */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">予約済みカテゴリ</span>
          {categoryOptions ? (
            <Select
              surface="white"
              aria-label="予約済みカテゴリ"
              disabled={value.neverBooked === true}
              value={value.bookedCategoryId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                update({ bookedCategoryId: v ? v : undefined });
              }}
            >
              <option value="">指定なし</option>
              {withCurrentValue(categoryOptions, value.bookedCategoryId).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              surface="white"
              type="text"
              aria-label="予約済みカテゴリ"
              placeholder="例: keeper"
              disabled={value.neverBooked === true}
              value={value.bookedCategoryId ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                update({ bookedCategoryId: v === '' ? undefined : v });
              }}
            />
          )}
        </label>

        {/* 行動ログ種別（hasEventType） */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">
            行動ログ（この行動の経験あり）
          </span>
          <Select
            surface="white"
            aria-label="行動ログ種別"
            value={value.hasEventType ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              update({
                hasEventType: v ? (v as PromoEventType) : undefined,
              });
            }}
          >
            <option value="">指定なし</option>
            {SEGMENT_EVENT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        {/* M-165: 流入経路（最初の入口）/ 新規の友だちのみ */}
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">
            流入経路（最初の入口）
          </span>
          <Select
            surface="white"
            aria-label="流入経路"
            value={value.firstSource ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              update({ firstSource: v ? (v as FirstSourceBucket) : undefined });
            }}
          >
            <option value="">指定なし</option>
            {FIRST_SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="block space-y-1">
          <span className="text-label font-medium text-ink-2">
            その経路での新規・既存
          </span>
          <Select
            surface="white"
            aria-label="流入経路の新規・既存"
            value={value.firstSourceNewOnly === true ? 'new' : ''}
            onChange={(e) => {
              update({ firstSourceNewOnly: e.target.value === 'new' ? true : undefined });
            }}
          >
            <option value="">新規・既存を問わない</option>
            <option value="new">新規の友だちのみ</option>
          </Select>
        </label>
      </div>

      <SurveyAnswerFilter
        value={value}
        onChange={onChange}
        {...(surveys ? { surveys } : {})}
      />

      <CouponHoldingFilter
        value={value}
        onChange={onChange}
        {...(couponOptions ? { options: couponOptions } : {})}
      />
    </fieldset>
  );
}

/** 状態の日本語表記（アンケート select で「受付中 / 下書き」を見分ける）。 */
const SURVEY_STATUS_LABELS: Readonly<Record<PromoSurvey['status'], string>> = {
  draft: '下書き',
  active: '受付中',
  archived: '受付終了',
};

/**
 * M-97: アンケートの回答内容で絞り込む条件（「オイル交換に興味」と答えた人）。
 *
 * **選択式の設問だけ**を対象にする — 判定材料が users ミラー（選択肢 id のみ）だからで、
 * 自由記述・年月・数値はそもそも条件にできない。理由を UI にも書く（黙って選べないと
 * 「設定できるはずなのに出てこない」になる）。
 *
 * アンケート / 設問の選択途中（選択肢をまだ選んでいない状態）は `SegmentSpec` に載せない
 * （`optionIds` が空の条件は誰にも一致しないため、中途半端な状態で配信されると事故になる）。
 * 途中経過はローカル state に持ち、選択肢が 1 つ以上選ばれた時点で親へ伝播する。
 */
function SurveyAnswerFilter({
  value,
  onChange,
  surveys,
}: {
  value: SegmentSpec;
  onChange: (next: SegmentSpec) => void;
  surveys?: readonly PromoSurvey[];
}) {
  const [localSurveyId, setLocalSurveyId] = useState('');
  const [localQuestionId, setLocalQuestionId] = useState('');

  const surveyId = value.surveyAnswer?.surveyId ?? localSurveyId;
  const questionId = value.surveyAnswer?.questionId ?? localQuestionId;
  const selectedOptionIds = value.surveyAnswer?.optionIds ?? [];

  // 条件が設定済みなのにアンケート一覧が無い（読み込み前 / 権限なし）ときは、
  // 条件が「見えないまま効いている」状態を作らないよう解除だけできるようにする。
  if (!surveys || surveys.length === 0) {
    if (!value.surveyAnswer) return null;
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] bg-surface-2 px-[22px] py-5">
        <span className="text-label text-ink-2">
          アンケート回答の条件が設定されています（アンケートを読み込めていません）
        </span>
        <button
          type="button"
          className="text-label font-medium text-brand underline"
          onClick={() => onChange({ ...value, surveyAnswer: undefined })}
        >
          条件を解除
        </button>
      </div>
    );
  }

  const survey = surveys.find((s) => s.id === surveyId);
  const questions = survey
    ? normalizePromoSurvey(survey).questions.filter(
        (q): q is PromoSurveyChoiceQuestion => q.type === 'choice',
      )
    : [];
  const question = questions.find((q) => q.id === questionId);

  const selectSurvey = (nextId: string): void => {
    setLocalSurveyId(nextId);
    setLocalQuestionId('');
    onChange({ ...value, surveyAnswer: undefined });
  };

  const selectQuestion = (nextId: string): void => {
    setLocalQuestionId(nextId);
    setLocalSurveyId(surveyId);
    onChange({ ...value, surveyAnswer: undefined });
  };

  const toggleOption = (optionId: string): void => {
    const next = selectedOptionIds.includes(optionId)
      ? selectedOptionIds.filter((id) => id !== optionId)
      : [...selectedOptionIds, optionId];
    setLocalSurveyId(surveyId);
    setLocalQuestionId(questionId);
    onChange({
      ...value,
      surveyAnswer:
        next.length > 0 ? { surveyId, questionId, optionIds: next } : undefined,
    });
  };

  return (
    <div className="space-y-3 rounded-[10px] bg-surface-2 px-[22px] py-5">
      <span className="text-label font-medium text-ink-2">アンケートの回答</span>

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block space-y-1">
          <span className="text-label text-ink-3">対象アンケート</span>
          <Select
            surface="white"
            aria-label="対象アンケート"
            value={surveyId}
            onChange={(e) => selectSurvey(e.target.value)}
          >
            <option value="">指定なし</option>
            {surveys.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}（{SURVEY_STATUS_LABELS[s.status]}）
              </option>
            ))}
          </Select>
        </label>

        {survey && (
          <label className="block space-y-1">
            <span className="text-label text-ink-3">設問（選択式のみ）</span>
            <Select
              surface="white"
              aria-label="アンケートの設問"
              value={questionId}
              onChange={(e) => selectQuestion(e.target.value)}
            >
              <option value="">指定なし</option>
              {questions.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.text}
                </option>
              ))}
            </Select>
          </label>
        )}
      </div>

      {question && (
        <div className="space-y-1">
          <span className="text-label text-ink-3">
            この選択肢を選んだお客様（いずれか 1 つでも一致すれば対象）
          </span>
          <div className="flex flex-wrap gap-3">
            {question.options.map((o) => (
              <label
                key={o.id}
                className="flex items-center gap-1.5 text-body text-ink-2"
              >
                <input
                  type="checkbox"
                  aria-label={o.label}
                  checked={selectedOptionIds.includes(o.id)}
                  onChange={() => toggleOption(o.id)}
                  className="rounded-sm text-brand focus:ring-brand"
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      )}

      <p className="text-note text-ink-3">
        条件に使えるのは<strong className="font-semibold">選択式の設問だけ</strong>
        です（自由記述・年月・数値は顧客情報に保存していないため対象外）。
        また、お客様ごとに
        <strong className="font-semibold">
          最新 {SURVEY_ANSWER_MIRROR_LIMIT} 件のアンケート
        </strong>
        までが対象で、それより前の回答では抽出されません。
      </p>
    </div>
  );
}

/**
 * M-166: クーポンの保有状態で絞る条件（「参加賞を取得したが未使用の人」）。
 *
 * 選択肢はルーレットの用途単位（複製 #01〜#10 は 1 つにまとめる / `couponIds` が OR）。
 * 「状態」はクーポンを選んだときだけ有効で、クーポンを外すと条件ごと消える（状態だけが
 * 残って「何の」未使用か分からない条件を作らない）。既定の状態は「取得して未使用」。
 */
function CouponHoldingFilter({
  value,
  onChange,
  options,
}: {
  value: SegmentSpec;
  onChange: (next: SegmentSpec) => void;
  options?: readonly CouponSegmentOption[];
}) {
  const current = value.couponHolding;
  const clear = (): void => {
    const { couponHolding: _omit, ...rest } = value;
    void _omit;
    onChange(rest);
  };

  if (!options) {
    // 条件が設定済みなのに選択肢が無い（ルーレット未設定 / 読み込み前）ときは、見えない条件が
    // 効いたままにならないよう解除導線だけ出す。
    if (!current || current.couponIds.length === 0) return null;
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-[10px] bg-surface-2 px-[22px] py-4 text-label text-ink-2">
        <span>
          クーポンの条件が設定されています（
          {current.label ?? `${current.couponIds.length} 種`}
          ）が、ルーレットの設定を読み込めていません
        </span>
        <button
          type="button"
          className="text-label font-medium text-brand underline"
          onClick={clear}
        >
          条件を外す
        </button>
      </div>
    );
  }

  const selected = findCouponSegmentOption(options, current);
  const selectValue = selected?.value ?? (current ? '__unknown__' : '');
  const state: CouponHoldingSegmentState = current?.state ?? 'claimed_unused';

  return (
    <div className="grid gap-4 rounded-[10px] bg-surface-2 px-[22px] py-5 sm:grid-cols-2">
      <label className="block space-y-1">
        <span className="text-label font-medium text-ink-2">クーポン（ルーレット）</span>
        <Select
          surface="white"
          aria-label="クーポン"
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            const opt = options.find((o) => o.value === v);
            if (!opt) {
              clear();
              return;
            }
            onChange({ ...value, couponHolding: buildCouponHoldingSegment(opt, state) });
          }}
        >
          <option value="">指定なし</option>
          {current && !selected ? (
            <option value="__unknown__">
              {current.label ?? `${current.couponIds.length} 種`}（マスタ外）
            </option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </label>
      <label className="block space-y-1">
        <span className="text-label font-medium text-ink-2">状態</span>
        <Select
          surface="white"
          aria-label="クーポンの状態"
          value={state}
          disabled={!current}
          onChange={(e) => {
            if (!current) return;
            onChange({
              ...value,
              couponHolding: {
                ...current,
                state: e.target.value as CouponHoldingSegmentState,
              },
            });
          }}
        >
          {COUPON_HOLDING_STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </label>
      <p className="text-note text-ink-3 sm:col-span-2">
        複製クーポン（#01〜）はまとめて 1
        つの選択肢です。「取得して未使用」は取得済みで店頭使用 0
        回の人（配信だけ受けて取得していない人は含みません）。
      </p>
    </div>
  );
}
